import { TextField, Paper, Typography, Box, Button, Grid, Skeleton, Switch, FormControlLabel, InputAdornment } from '@mui/material';
import React, { useEffect, useState, useTransition, useMemo, useCallback, useRef } from 'react';
import { gamePointManagementApi } from '../../services/AllServices';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import errorHandler from '../../utils/errorHandler';
import { FixedSizeList as List } from 'react-window';
import CardLayout from '../../layouts/CardLayout';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import { styles } from './AddEdit.styles';

const roles = ['forward', 'midfielder', 'defender', 'goalkeeper'];

const Row = React.memo(({ index, style, data }) => {
  const { scoringEntries, handleChange, handleStatusChange } = data;
  const [eventKey, eventData] = scoringEntries[index];

  return (
    <Grid
      container
      spacing={1}
      sx={{
        ...style,
        ...styles.rowContainer
      }}
    >
      <Grid item xs={2.5}>
        <Typography sx={styles.eventNameText}>
          {eventData?.eventName}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Box sx={styles.statsGroupContainer}>
          <Typography sx={styles.statsGroupText}>
            {eventData?.eventStatsGroup}
          </Typography>
        </Box>
      </Grid>
      {roles.map((roleKey) => (
        <Grid item xs={1.5} key={roleKey}>
          <TextField
            type="text"
            size="small"
            fullWidth
            inputProps={{ inputMode: 'decimal' }}
            value={eventData[roleKey] || ''}
            onChange={(e) => handleChange(e, eventKey, roleKey)}
            sx={styles.rowInputField}
          />
        </Grid>
      ))}
      <Grid item xs={1.5}>
        <FormControlLabel
          control={
            <Switch
              checked={eventData?.status === 1}
              onChange={(e) => handleStatusChange(eventKey, e.target.checked ? 1 : 0)}
              size="small"
              sx={styles.activeSwitch}
            />
          }
          label={
            <Typography sx={{
              ...styles.switchLabel,
              ...(eventData?.status === 1 ? styles.activeSwitchLabel : styles.inactiveSwitchLabel)
            }}>
              {eventData?.status === 1 ? 'Active' : 'Inactive'}
            </Typography>
          }
          sx={styles.switchContainer}
        />
      </Grid>
    </Grid>
  );
});

const AddEditGamePoint = () => {
  const [scoring, setScoring] = useState({});
  const [gameDurationType, setGameDurationType] = useState('');
  const [captainMultiplier, setCaptainMultiplier] = useState('');
  const [viceCaptainMultiplier, setViceCaptainMultiplier] = useState('');
  const [contestType, setContestType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [listKey, setListKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const itemDataRef = useRef({ scoringEntries: [], handleChange: null, handleStatusChange: null });

  useEffect(() => {
    const fetchScoring = async () => {
      try {
        setIsLoading(true);
        const response = await gamePointManagementApi.getById({ id });
        const data = response?.data?.data;
        if (data?.scoring) {
          startTransition(() => {
            const scoringObj = {};
            data.scoring.forEach((eventData) => {
              const cleanEvent = { ...eventData };
              roles.forEach((r) => {
                if (cleanEvent[r] == null) cleanEvent[r] = '';
                else cleanEvent[r] = String(cleanEvent[r]);
              });

              if (cleanEvent.status === undefined || cleanEvent.status === null) {
                cleanEvent.status = 1;
              }
              scoringObj[eventData.eventKey || eventData.eventName] = cleanEvent;
            });
            setScoring(scoringObj);
            setGameDurationType(data.gameDurationType || '');
            setCaptainMultiplier(data.captainMultiplier?.toString() || '');
            setViceCaptainMultiplier(data.viceCaptainMultiplier?.toString() || '');
            setContestType(data.contestType || '');
          });
        }
      } catch (error) {
        errorHandler(error);
        navigate('/game-point-management');
      } finally {
        setIsLoading(false);
      }
    };
    fetchScoring();
  }, [id, navigate]);

  const validNumberRegex = /^-?(\d+(\.\d*)?|\.\d+)?$/;

  const handleChange = useCallback((event, eventKey, roleKey) => {
    const value = event.target.value;
    if (validNumberRegex.test(value) || value === '') {
      setScoring((prev) => {
        if (prev[eventKey] && prev[eventKey][roleKey] === value) return prev;
        return {
          ...prev,
          [eventKey]: {
            ...prev[eventKey],
            [roleKey]: value
          }
        };
      });
    }
  }, []);

  const handleCaptainMultiplierChange = (setter) => (event) => {
    const value = event.target.value;
    if (validNumberRegex.test(value) || value === '') {
      setter(value);
    }
  };

  const handleStatusChange = useCallback(async (eventKey, newStatus) => {
    try {
      setScoring((prev) => ({
        ...prev,
        [eventKey]: {
          ...prev[eventKey],
          status: newStatus
        }
      }));

      const eventData = scoring[eventKey];
      if (eventData && eventData.eventTypeId) {
        const payload = {
          id: id, 
          eventTypeId: eventData.eventTypeId,
          status: newStatus
        };
        
        await gamePointManagementApi.pointStatus(payload);
        toast.success(`Status updated to ${newStatus === 1 ? 'Active' : 'Inactive'}`);
      }
    } catch (error) {
      setScoring((prev) => ({
        ...prev,
        [eventKey]: {
          ...prev[eventKey],
          status: prev[eventKey]?.status === 1 ? 0 : 1
        }
      }));
      errorHandler(error);
    }
  }, [scoring]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const scoringArray = Object.entries(scoring).map(([eventKey, eventData]) => {
        const numericEventData = { ...eventData };
        roles.forEach((role) => {
          numericEventData[role] = parseFloat(eventData[role]) || 0;
        });
        return numericEventData;
      });
      const payload = {
        id,
        contestType,
        scoring: scoringArray,
        captainMultiplier: parseFloat(captainMultiplier) || 0,
        viceCaptainMultiplier: parseFloat(viceCaptainMultiplier) || 0,
        gameDurationType
      };
      await gamePointManagementApi.edit(payload);
      toast.success('Scoring rules updated successfully');
      
      // Navigate back to game point management with preserved page and view mode
      const page = searchParams.get('page');
      const view = searchParams.get('view');
      
      if (page && view) {
        navigate(`/game-point-management?page=${page}&view=${view}`);
      } else if (page) {
        navigate(`/game-point-management?page=${page}`);
      } else if (view) {
        navigate(`/game-point-management?view=${view}`);
      } else {
        navigate("/game-point-management");
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  const scoringEntries = useMemo(() => {
    const entries = Object.entries(scoring);
    if (!searchQuery.trim()) return entries;
    
    return entries.filter(([eventKey, eventData]) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        eventData?.eventName?.toLowerCase().includes(searchLower) ||
        eventData?.eventStatsGroup?.toLowerCase().includes(searchLower) ||
        roles.some(role => 
          eventData[role]?.toString().toLowerCase().includes(searchLower)
        )
      );
    });
  }, [scoring, searchQuery]);

  useMemo(() => {
    itemDataRef.current = {
      scoringEntries,
      handleChange,
      handleStatusChange
    };
  }, [scoringEntries, handleChange, handleStatusChange]);

  return (
    <>
      <Helmet>
        <title>Edit Scoring Rules | Squad Duel Admin</title>
      </Helmet>

      <CardLayout>
        <Box sx={styles.headerContainer}>
          <Typography variant="h4" gutterBottom sx={styles.headerTitle}>
            🎯 Edit Scoring Rules
          </Typography>
          <Typography variant="body1" sx={styles.headerSubtitle}>
            Configure point values and multipliers for different player roles and events
          </Typography>
        </Box>

        <Paper sx={styles.globalSettingsContainer}>
          <Box sx={styles.globalSettingsHeader}>
            <Box sx={styles.globalSettingsIcon}>
              <Typography sx={styles.globalSettingsIconText}>⚙️</Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={styles.globalSettingsTitle}>
                Global Settings
              </Typography>
              <Typography variant="body2" sx={styles.globalSettingsSubtitle}>
                Configure multipliers and contest settings
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.captainFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>👑</Typography>
                  <Typography variant="subtitle2" sx={styles.captainFieldLabel}>
                    Captain Multiplier
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="Enter multiplier value"
                  inputProps={{ inputMode: 'decimal' }}
                  value={captainMultiplier}
                  onChange={handleCaptainMultiplierChange(setCaptainMultiplier)}
                  sx={{ ...styles.inputField, ...styles.captainInputField }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.viceCaptainFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>🥈</Typography>
                  <Typography variant="subtitle2" sx={styles.viceCaptainFieldLabel}>
                    Vice Captain Multiplier
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="Enter multiplier value"
                  inputProps={{ inputMode: 'decimal' }}
                  value={viceCaptainMultiplier}
                  onChange={handleCaptainMultiplierChange(setViceCaptainMultiplier)}
                  sx={{ ...styles.inputField, ...styles.viceCaptainInputField }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.contestTypeFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>🏆</Typography>
                  <Typography variant="subtitle2" sx={styles.contestTypeFieldLabel}>
                    Contest Type
                  </Typography>
                </Box>
                <TextField 
                  disabled 
                  fullWidth 
                  value={contestType}
                  sx={{ ...styles.inputField, ...styles.contestTypeInputField }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Search Input */}
        <Box sx={styles.searchContainer}>
          <Typography variant="h6" sx={styles.searchTitle}>
            🔍 Search Scoring Events
          </Typography>
          <TextField
            fullWidth
            placeholder="Search by event name, stats group, or point values..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            }}
            size="medium"
            sx={styles.searchInput}
          />
          {searchQuery && (
            <Box sx={styles.searchResults}>
              <Typography variant="body2" sx={styles.searchResultsText}>
                ✨ Found {scoringEntries.length} matching entries
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={styles.tableHeader}>
          <Box sx={{ width: '20%', ...styles.tableHeaderCell }}>
            <strong>Event Name</strong>
          </Box>
          <Box sx={{ width: '15%', ...styles.tableHeaderCell }}>
            <strong>Stats Group</strong>
          </Box>
          {roles.map((role) => (
            <Box key={role} sx={{ width: '12%', ...styles.tableHeaderCell }}>
              <strong>{role.charAt(0).toUpperCase() + role.slice(1)}</strong>
            </Box>
          ))}
          <Box sx={{ width: '15%', ...styles.tableHeaderCell }}>
            <strong>Status</strong>
          </Box>
        </Box>

        <Paper sx={styles.tableContainer}>
          {isLoading || isPending ? (
            [...Array(10)].map((_, i) => (
              <Box key={i} sx={{ px: 2, py: 1 }}>
                <Skeleton height={30} />
              </Box>
            ))
          ) : scoringEntries.length === 0 ? (
            <Box sx={styles.emptyStateContainer}>
              <Typography variant="h6" sx={styles.emptyStateTitle}>
                {searchQuery ? '🔍 No matching entries found' : '📊 No scoring entries available'}
              </Typography>
              <Typography variant="body2" sx={styles.emptyStateText}>
                {searchQuery ? 'Try adjusting your search terms or clearing the search.' : 'Add some scoring events to get started.'}
              </Typography>
            </Box>
          ) : (
            <List
              height={1400}
              itemCount={scoringEntries.length}
              itemSize={70}
              width="100%"
              itemData={itemDataRef.current}
            >
              {Row}
            </List>
          )}
        </Paper>

        <Box sx={styles.saveButtonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            size="large"
            sx={styles.saveButton}
          >
            {isLoading ? '💾 Saving...' : '💾 Save Changes'}
          </Button>
        </Box>
      </CardLayout>
    </>
  );
};

export default AddEditGamePoint;